import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <SchemaOrg
        type="Article"
        articleData={{
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          datePublished: article.publishDate,
          dateModified: article.updateDate,
          authorName: article.author,
          url: `https://www.guochanyiqu.com/article/${article.slug}`,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 国产一区二区精选内容导航平台`,
          description: article.excerpt,
          url: `https://www.guochanyiqu.com/article/${article.slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Article Header */}
          <article className="pt-20 pb-8">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full cursor-pointer whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>

                <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                  {article.excerpt}
                </p>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    发布于 {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime} 分钟阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次浏览
                  </span>
                </div>
              </div>
            </div>
          </article>

          {/* Featured Image */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
            <div className="rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                title={`${article.title} - 国产一区二区精选`}
                className="w-full object-cover max-h-[500px]"
              />
            </div>
          </section>

          {/* Article Content */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 pb-10">
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </section>

          {/* Article Footer */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 pb-10">
            <div className="border-t border-background-200 pt-6">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-700 rounded-full border border-background-200 hover:border-primary-300 cursor-pointer whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-background-100 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                    <p className="text-xs text-foreground-500">国产一区二区内容编辑部</p>
                  </div>
                </div>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看更多{article.category}
                </Link>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}